import type { ComponentType, LazyExoticComponent } from 'react';
import type { z } from 'zod';

/**
 * The widget contract, TechSpec §9.1 / §9.1.1. A widget is one folder under
 * src/widgets/<type>/ whose index.ts default-exports `defineWidget({...})`.
 */

export type WidgetCategory = 'metric' | 'event' | 'spatial' | 'composite' | 'ai' | 'action';

export interface Size {
  w: number;
  h: number;
}

export type WidgetStatus = 'loading' | 'ready' | 'empty' | 'error' | 'stale';

export interface WidgetDataState<T> {
  status: WidgetStatus;
  data: T | null;
  error?: Error;
  /** When the newest datum was produced at the source — shown when stale. */
  lastUpdated?: Date;
  /** Refetch, for the error state's retry button. */
  retry?: () => void;
}

export interface RenderProps<P, D> {
  props: P;
  data: D;
  /** Grid units. */
  size: Size;
}

export interface EditorProps<P> {
  /** Draft props: may be incomplete until the schema accepts them. */
  props: Partial<P>;
  onChange: (props: Partial<P>) => void;
}

// The schema's input may differ from P (defaults); only its output matters here.
type PropsSchema<P> = z.ZodType<P, unknown>;

export interface WidgetDefinition<P = Record<string, unknown>, D = unknown> {
  /** Folder name; also the i18n namespace `widget.<type>` (name, strings). */
  readonly type: string;
  readonly category: WidgetCategory;
  readonly defaultSize: Size;
  readonly minSize: Size;
  readonly propsSchema: PropsSchema<P>;
  /** Pre-filled when the widget is added. */
  readonly defaults: Partial<P>;
  /** Which sources feed this instance: health badge and drill-down (VS-015, VS-044). */
  readonly sourceIds: (props: P) => string[];
  /** Data binding through SDK hooks only (§9.2 rule 5). Called on every render: a hook. */
  readonly useData: (props: P) => WidgetDataState<D>;
  /** Called only with data: loading, empty and error belong to the frame. */
  readonly Render: LazyExoticComponent<ComponentType<RenderProps<P, NonNullable<D>>>>;
  readonly Editor: LazyExoticComponent<ComponentType<EditorProps<P>>>;
}

/** Identity with inference: the manifest stays a plain object. */
export function defineWidget<P, D>(definition: WidgetDefinition<P, D>): WidgetDefinition<P, D> {
  return definition;
}

/** A definition with its types erased, as the registry and the frame hold it. */
// biome-ignore lint/suspicious/noExplicitAny: the registry holds widgets of every props/data type; each is validated by its own schema before use
export type AnyWidget = WidgetDefinition<any, any>;
